import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Analytics } from '@vercel/analytics/react';
import { AppProvider, useApp } from './context/AppContext';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CapexConfigurator from './pages/CapexConfigurator';
import OpexCalculator from './pages/OpexCalculator';
import BusinessPlan from './pages/BusinessPlan';
import Wiki from './pages/Wiki';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import FollowupPage from './pages/FollowupPage';
import CargoDroneCalculatorLanding from './pages/CargoDroneCalculatorLanding';
import PartnerClients from './pages/PartnerClients';
import PartnerFAQ from './pages/PartnerFAQ';
import PartnerContent from './pages/PartnerContent';
import './index.css';
import useIsMobile from './hooks/useIsMobile';

function ProtectedLayout({ children }) {
  const { token } = useApp();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  if (!token) return <Navigate to="/login" replace />;
  return (
    <div className="app-shell" style={{ display:'flex' }}>
      {isMobile && (
        <div className="mobile-topbar">
          <button className="mobile-menu-btn" onClick={() => setMenuOpen((v) => !v)} aria-label={menuOpen ? 'Close menu' : 'Open menu'}>
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <img src="/images/drones/logo.png" alt="FlyingBasket Logo" style={{ height: 28, width:'auto', objectFit:'contain' }} />
        </div>
      )}
      <Sidebar isMobile={isMobile} isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
      <div className="main-content">{children}</div>
    </div>
  );
}

function AdminLayout({ children }) {
  const { token, user } = useApp();
  if (!token) return <Navigate to="/login" replace />;
  if (user?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <div style={{ padding: '1rem' }}>{children}</div>;
}

function PartnerLayout({ children }) {
  const { token, user } = useApp();
  if (!token) return <Navigate to="/login" replace />;
  if (user?.role !== 'partner' && user?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <ProtectedLayout>{children}</ProtectedLayout>;
}

function AppRoutes() {
  const { token } = useApp();
  return (
    <Routes>
      <Route path="/login" element={token ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/cargo-drone-calculator" element={<CargoDroneCalculatorLanding />} />
      <Route path="/dashboard" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
      <Route path="/capex" element={<ProtectedLayout><CapexConfigurator /></ProtectedLayout>} />
      <Route path="/opex" element={<ProtectedLayout><OpexCalculator /></ProtectedLayout>} />
      <Route path="/business-plan" element={<ProtectedLayout><BusinessPlan /></ProtectedLayout>} />
      <Route path="/wiki" element={<ProtectedLayout><Wiki /></ProtectedLayout>} />
      <Route path="/settings" element={<ProtectedLayout><Settings /></ProtectedLayout>} />
      <Route path="/admin/*" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
      <Route path="/followup" element={<Navigate to="/admin" replace />} />
      <Route path="/partner/clients" element={<PartnerLayout><PartnerClients /></PartnerLayout>} />
      <Route path="/partner/faq" element={<PartnerLayout><PartnerFAQ /></PartnerLayout>} />
      <Route path="/partner/content" element={<PartnerLayout><PartnerContent /></PartnerLayout>} />
      <Route path="*" element={<Navigate to={token ? "/dashboard" : "/login"} />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppRoutes />
        <Analytics />
      </BrowserRouter>
    </AppProvider>
  );
}
